import React from 'react';

function About() {
  return (
    <div className="container">
      <h1 style={{ marginBottom: '2rem' }}>About Sidekick</h1>

      <div className="card">
        <h2 style={{ marginBottom: '1rem' }}>What is Sidekick?</h2>
        <p style={{ marginBottom: '1rem', lineHeight: '1.6' }}>
          Sidekick is a comprehensive drug knowledge graph that integrates information from FDA 
          Structured Product Labels (SPLs) with standardized biomedical ontologies. Our goal is 
          to make drug safety and efficacy information more accessible and queryable for researchers, 
          clinicians, and developers.
        </p>
        <p style={{ lineHeight: '1.6' }}>
          The knowledge graph contains over 3 million triples covering drug indications, 
          contraindications, side effects, and drug-drug interactions, all mapped to standard 
          terminologies for interoperability.
        </p>
      </div>

      <div className="card">
        <h2 style={{ marginBottom: '1rem' }}>Data Sources</h2>
        <h3 style={{ marginBottom: '0.5rem', fontSize: '1rem' }}>FDA Structured Product Labels</h3>
        <p style={{ marginBottom: '1rem', lineHeight: '1.6', fontSize: '0.875rem' }}>
          All drug information is extracted from FDA-approved Structured Product Labels available 
          through DailyMed. These labels contain official prescribing information including indications, 
          contraindications, warnings, and adverse reactions.
        </p>

        <h3 style={{ marginBottom: '0.5rem', fontSize: '1rem' }}>Ontologies Used</h3>
        <ul style={{ marginLeft: '1.5rem', lineHeight: '1.8', fontSize: '0.875rem' }}>
          <li><strong>SIO (Semantic Science Integrated Ontology)</strong>: Upper-level ontology providing the structural framework</li>
          <li><strong>HPO (Human Phenotype Ontology)</strong>: Standardized terminology for phenotypic abnormalities</li>
          <li><strong>MONDO</strong>: Comprehensive disease ontology</li>
          <li><strong>RxNorm</strong>: Normalized nomenclature for clinical drugs</li>
        </ul>
      </div>

      <div className="card">
        <h2 style={{ marginBottom: '1rem' }}>Architecture</h2>
        <p style={{ marginBottom: '1rem', lineHeight: '1.6' }}>
          Sidekick uses a hub-and-spoke model where:
        </p>
        <ul style={{ marginLeft: '1.5rem', lineHeight: '1.8' }}>
          <li>
            <strong>Drug Collections</strong> serve as hubs, representing unique combinations of 
            active ingredients
          </li>
          <li>
            <strong>Products</strong> (branded formulations) link to their ingredient collections
          </li>
          <li>
            <strong>Associations</strong> connect drug collections to clinical findings (side effects, 
            indications, contraindications)
          </li>
          <li>
            <strong>SPL Documents</strong> provide provenance for all associations
          </li>
        </ul>
      </div>

      <div className="card">
        <h2 style={{ marginBottom: '1rem' }}>Features</h2>
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div>
            <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>🔍 Multi-mode Search</h3>
            <p style={{ fontSize: '0.875rem', lineHeight: '1.6' }}>
              Search by ingredient name, RxCUI, or product name with intelligent autocomplete
            </p>
          </div>
          <div>
            <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>💊 Detailed Drug Profiles</h3>
            <p style={{ fontSize: '0.875rem', lineHeight: '1.6' }}>
              View comprehensive information including products, side effects, indications, and 
              contraindications organized into 9 dedicated tabs
            </p>
          </div>
          <div>
            <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>🔬 SPARQL Interface</h3>
            <p style={{ fontSize: '0.875rem', lineHeight: '1.6' }}>
              Run custom queries against the knowledge graph with pre-loaded examples
            </p>
          </div>
          <div>
            <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>📄 Provenance</h3>
            <p style={{ fontSize: '0.875rem', lineHeight: '1.6' }}>
              Every association links back to its source SPL document for verification
            </p>
          </div>
        </div>
      </div>

      <div className="card">
        <h2 style={{ marginBottom: '1rem' }}>Team</h2>
        <ul style={{ listStyle: 'none', padding: 0, lineHeight: '1.8' }}>
          <li>Mohammad Ashhad - King Abdullah University of Science and Technology (KAUST)</li>
          <li>Olga Mashkova - King Abdullah University of Science and Technology (KAUST)</li>
          <li>Ricardo Henao - Duke University</li>
          <li>Robert Hoehndorf - King Abdullah University of Science and Technology (KAUST)</li>
        </ul>
      </div>

      <div className="card">
        <h2 style={{ marginBottom: '1rem' }}>License</h2>
        <p style={{ lineHeight: '1.6' }}>
          Sidekick is released under the{' '}
          <a 
            href="https://creativecommons.org/licenses/by/4.0/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="link"
          >
            Creative Commons Attribution 4.0 International License
          </a>.
        </p>
      </div>

      <div className="card">
        <h2 style={{ marginBottom: '1rem' }}>Technical Details</h2>
        <table style={{ fontSize: '0.875rem' }}>
          <tbody>
            <tr>
              <td style={{ fontWeight: 600 }}>Backend</td>
              <td>FastAPI (Python)</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 600 }}>SPARQL Endpoint</td>
              <td>Apache Jena Fuseki</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 600 }}>Frontend</td>
              <td>React</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 600 }}>Ontology Format</td>
              <td>RDF/Turtle (OWL 2)</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 600 }}>Total Triples</td>
              <td>~3.2 million</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2 style={{ marginBottom: '1rem' }}>Contact & Feedback</h2>
        <p style={{ lineHeight: '1.6' }}>
          For questions, bug reports, or suggestions, please contact:{' '}
          <a href="mailto:mohammad.ashhad@kaust.edu.sa" className="link">
            mohammad.ashhad@kaust.edu.sa
          </a>
        </p>
      </div>
    </div>
  );
}

export default About;
